import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { fetchAuthSession } from 'aws-amplify/auth';
import { from, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { SubtenantService } from '../common/services/subtenant.service';

const AUTH_BOOTSTRAP_PATHS = [
  '/user/select-subtenant',
  '/user/subtenants',
  '/user/find-by-email',
  '/user/align-user'
];

function withAuthHeader(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  return token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.url.includes('/assets/i18n/')) {
    return next(req);
  }

  const subtenantService = inject(SubtenantService);
  const isBootstrapRequest = AUTH_BOOTSTRAP_PATHS.some(path => req.url.includes(path));

  const cognitoToken$ = from(fetchAuthSession()).pipe(
    map(session => session.tokens?.accessToken?.toString() ?? null),
    catchError(err => {
      if (err instanceof HttpErrorResponse) {
        return throwError(() => err);
      }
      console.warn('authInterceptor: nessuna sessione Cognito, proseguo senza token Cognito.', err);
      return of<string | null>(null);
    })
  );

  return cognitoToken$.pipe(
    switchMap(cognitoToken => {
      if (isBootstrapRequest) {
        return next(withAuthHeader(req, cognitoToken));
      }

      const hasSubtenant = !!subtenantService.getSubtenantToken();

      return subtenantService.getValidSubtenantToken().pipe(
        map(subtenantToken => subtenantToken ?? cognitoToken),
        switchMap(token =>
          next(withAuthHeader(req, token)).pipe(
            catchError((error: HttpErrorResponse) => {
              if (error.status === 401 && hasSubtenant) {
                return subtenantService.refreshSubtenantToken().pipe(
                  switchMap(newToken =>
                    newToken
                      ? next(withAuthHeader(req, newToken))
                      : throwError(() => error)
                  )
                );
              }
              return throwError(() => error);
            })
          )
        )
      );
    })
  );
};
